import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ShoppingBag, Search, Heart, User, Sparkles, Star, Shield, Truck, Droplet, Sun, Moon } from 'lucide-react';

export default function ProductGuide() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = React.useState('');
  const [cart, setCart] = React.useState<{quantity: number}>({ quantity: 0 });
  const [showPromo, setShowPromo] = React.useState(true);
  const [hoveredProduct, setHoveredProduct] = React.useState<string | null>(null);

  const addToCart = (productId: string) => {
    setCart(prev => ({ quantity: prev.quantity + 1 }));
    alert('Product added to cart!');
  };

  const products = [
    {
      id: 'morning-glow',
      name: 'Morning Glow Lotion',
      price: 45,
      rating: 4.8,
      reviews: 124,
      image: 'https://images.unsplash.com/photo-1608248597279-f99d160bfcbc?auto=format&fit=crop&q=80',
      description: 'Our signature tallow-based lotion for radiant, healthy skin. Handcrafted with premium grass-fed tallow, organic oils, and natural ingredients.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-gradient-to-r from-espresso-900 via-clay-800 to-espresso-900 text-white relative overflow-hidden">
        {showPromo && (
          <div className="container mx-auto px-6 py-3 relative">
            <div className="flex items-center justify-center space-x-2 animate-float">
              <Sparkles className="w-4 h-4 text-gold-400" />
              <p className="text-center text-sm font-display">Free Shipping on Orders Over $100</p>
              <Sparkles className="w-4 h-4 text-gold-400" />
            </div>
            <button 
              onClick={() => setShowPromo(false)}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 text-cream-300 hover:text-white"
            >
              ×
            </button>
          </div>
        )}
        <nav className="border-b border-espresso-800">
          <div className="container mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <button 
                onClick={() => navigate('/')} 
                className="flex items-center space-x-2 group"
              >
                <div className="relative">
                  <Sparkles className="w-8 h-8 text-gold-400 transform group-hover:scale-110 transition-transform" />
                  <div className="absolute inset-0 bg-gold-400 opacity-50 blur-lg group-hover:opacity-75 transition-opacity"></div>
                </div>
                <span className="text-2xl font-serif group-hover:text-gold-400 transition-colors">Lola's Glow</span>
              </button>
              
              <div className="flex-1 max-w-xl mx-8">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search our natural skincare collection..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full px-4 py-3 bg-espresso-800/50 backdrop-blur-sm text-white placeholder-espresso-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold-400 transition-all"
                  />
                  <Search className="absolute right-3 top-2.5 w-5 h-5 text-espresso-400" />
                </div>
              </div>
              
              <div className="flex items-center space-x-6">
                <button className="hover:text-gold-400 transition-colors">
                  <Heart className="w-6 h-6" />
                </button>
                <button className="hover:text-gold-400 transition-colors">
                  <User className="w-6 h-6" />
                </button>
                <button className="relative hover:text-gold-400 transition-colors">
                  <ShoppingBag className="w-6 h-6" />
                  {cart.quantity > 0 && (
                    <span className="absolute -top-2 -right-2 bg-clay-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {cart.quantity}
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </nav>
      </header>

      <div className="container mx-auto px-6 py-8">
        {/* Hero Section */}
        <div className="relative mb-16 rounded-2xl overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-espresso-900/90 to-espresso-900/40"></div>
          <img 
            src="https://images.unsplash.com/photo-1576426863848-c21f53c60b19?auto=format&fit=crop&q=80" 
            alt="Natural Beauty" 
            className="w-full h-96 object-cover"
          />
          <div className="absolute inset-0 flex items-center">
            <div className="container mx-auto px-6">
              <div className="max-w-lg">
                <h1 className="text-5xl font-serif text-white mb-4">Morning Glow Lotion</h1>
                <p className="text-lg text-cream-100 mb-8">Experience the power of traditional tallow-based skincare, handcrafted with premium grass-fed ingredients.</p>
                <div className="flex space-x-4">
                  <button className="bg-clay-500 text-white px-8 py-3 rounded-lg hover:bg-clay-600 transition-colors">
                    Buy Now - $45
                  </button>
                  <button className="bg-white/20 backdrop-blur-sm text-white px-8 py-3 rounded-lg hover:bg-white/30 transition-colors">
                    Learn More
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Products Grid */}
        <div className="max-w-4xl mx-auto">
          {products
            .filter(product => 
              searchQuery === '' || 
              product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
              product.description.toLowerCase().includes(searchQuery.toLowerCase())
            )
            .map(product => (
              <div 
                key={product.id} 
                className="bg-white rounded-lg shadow-lg overflow-hidden group transform hover:-translate-y-1 transition-all duration-300"
                onMouseEnter={() => setHoveredProduct(product.id)}
                onMouseLeave={() => setHoveredProduct(null)}
              >
                <div className="relative">
                  <img 
                    src={product.image} 
                    alt={product.name}
                    className={`w-full h-64 object-cover transition-all duration-500 ${
                      hoveredProduct === product.id ? 'scale-110 blur-sm' : ''
                    }`}
                  />
                  {hoveredProduct === product.id && (
                    <div className="absolute inset-0 bg-espresso-900/70 backdrop-blur-sm flex items-center justify-center p-6 transform transition-all duration-300">
                      <p className="text-white text-center">{product.description}</p>
                    </div>
                  )}
                  <button 
                    className="absolute top-4 right-4 p-2 rounded-full bg-white/80 hover:bg-white transition-colors"
                    onClick={() => alert('Added to wishlist!')}
                  >
                    <Heart className="w-5 h-5 text-espresso-900" />
                  </button>
                </div>
                <div className="p-6">
                  <div className="flex items-center mb-2">
                    <Star className="w-4 h-4 text-gold-500 fill-current" />
                    <span className="ml-1 text-sm text-espresso-700">{product.rating}</span>
                    <span className="ml-1 text-sm text-espresso-500">({product.reviews})</span>
                  </div>
                  <h3 className="text-lg font-semibold text-espresso-900 mb-2">{product.name}</h3>
                  <p className="text-espresso-600 text-sm mb-4">{product.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xl font-semibold text-espresso-900">${product.price}</span>
                    <button
                      onClick={() => addToCart(product.id)}
                      className="bg-clay-600 text-white px-6 py-3 rounded-lg hover:bg-clay-700 transition-colors flex items-center"
                    >
                      <ShoppingBag className="w-5 h-5 mr-2" />
                      Add to Cart
                    </button>
                  </div>
                </div>
              </div>
            ))}
        </div>

        {/* Features */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16 bg-gradient-to-r from-cream-50 via-white to-cream-50 p-8 rounded-2xl shadow-lg border border-cream-100">
          <div className="flex items-center space-x-4">
            <Truck className="w-8 h-8 text-espresso-900" />
            <div>
              <h3 className="font-semibold text-espresso-900">Free Shipping</h3>
              <p className="text-sm text-espresso-600">On orders over $100</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <Shield className="w-8 h-8 text-espresso-900" />
            <div>
              <h3 className="font-semibold text-espresso-900">Secure Payment</h3>
              <p className="text-sm text-espresso-600">100% secure checkout</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <Star className="w-8 h-8 text-espresso-900" />
            <div>
              <h3 className="font-semibold text-espresso-900">Quality Promise</h3>
              <p className="text-sm text-espresso-600">Satisfaction guaranteed</p>
            </div>
          </div>
        </div>

        {/* Day/Night Benefits */}
        <div className="mt-16">
          <div className="bg-gradient-to-br from-gold-100 to-cream-100 p-8 rounded-2xl relative overflow-hidden group">
            <div className="absolute top-4 right-4">
              <Sun className="w-12 h-12 text-gold-500 transform group-hover:rotate-180 transition-transform duration-700" />
            </div>
            <h3 className="text-2xl font-serif text-espresso-900 mb-4">The Power of Tallow</h3>
            <p className="text-espresso-700 mb-6">Our Morning Glow Lotion harnesses the nourishing properties of grass-fed tallow, rich in vitamins A, D, E, K, and B12. Combined with organic oils, it provides deep moisturization and skin-identical nutrients.</p>
            <button className="text-espresso-900 font-semibold group-hover:text-clay-600 transition-colors">
              Learn More →
            </button>
          </div>
        </div>

        {/* Product Details */}
        <div className="grid md:grid-cols-2 gap-12 mt-16">
            <div>
              <img 
                src="https://images.unsplash.com/photo-1608248597279-f99d160bfcbc?auto=format&fit=crop&q=80" 
                alt="Morning Glow Lotion" 
                className="rounded-lg shadow-lg w-full h-64 object-cover mb-8"
              />
              
              <div className="bg-cream-100 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-espresso-800 mb-4">Quick Facts</h3>
                <ul className="space-y-3">
                  <li className="flex items-center text-espresso-700">
                    <span className="w-2 h-2 bg-clay-500 rounded-full mr-3"></span>
                    100% Natural Ingredients
                  </li>
                  <li className="flex items-center text-espresso-700">
                    <span className="w-2 h-2 bg-clay-500 rounded-full mr-3"></span>
                    12-Month Shelf Life
                  </li>
                  <li className="flex items-center text-espresso-700">
                    <span className="w-2 h-2 bg-clay-500 rounded-full mr-3"></span>
                    Suitable for All Skin Types
                  </li>
                  <li className="flex items-center text-espresso-700">
                    <span className="w-2 h-2 bg-clay-500 rounded-full mr-3"></span>
                    Cruelty-Free
                  </li>
                </ul>
              </div>
            </div>

            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-espresso-900 mb-4">How to Use</h2>
                <div className="space-y-4 text-espresso-700">
                  <p>For best results, follow these steps:</p>
                  <ol className="list-decimal pl-5 space-y-3">
                    <li>Cleanse your face thoroughly</li>
                    <li>Pat skin until slightly damp</li>
                    <li>Apply a small amount to your fingertips</li>
                    <li>Gently massage using upward circular motions</li>
                    <li>Wait 2-3 minutes before other products</li>
                    <li>Use morning and night</li>
                  </ol>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-espresso-900 mb-4">Storage & Care</h2>
                <ul className="list-disc pl-5 space-y-3 text-espresso-700">
                  <li>Store in a cool, dry place</li>
                  <li>Keep away from direct sunlight</li>
                  <li>Use within 12 months of opening</li>
                  <li>Check the bottom for expiration date</li>
                  <li>Keep lid tightly closed when not in use</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-espresso-900 mb-4">Important Precautions</h2>
                <div className="bg-clay-50 border border-clay-200 p-6 rounded-lg">
                  <ul className="space-y-3 text-espresso-700">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-clay-500 rounded-full mr-3 mt-2"></span>
                      <span>Always perform a patch test before first use by applying a small amount to your inner arm</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-clay-500 rounded-full mr-3 mt-2"></span>
                      <span>Avoid contact with eyes. If contact occurs, rinse thoroughly with water</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-clay-500 rounded-full mr-3 mt-2"></span>
                      <span>Not recommended for use during active sunburn or on broken skin</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-clay-500 rounded-full mr-3 mt-2"></span>
                      <span>Discontinue use if irritation occurs and consult your healthcare provider</span>
                    </li>
                  </ul>
                </div>
              </section>
            </div>
         </div>
      </div>
    </div>
  );
}